import cn from 'classnames'

import { Translation } from 'cng-common/features/translations'
import { QualityGauge } from 'partner360/components/qualityIndicator'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../../configs/translations'
import { formatCockpitScore } from '../../../../../../lib'
import type { ICockpitPlanningStatus } from '../../../../../../model/hooks'
import { useCountUp } from '../../hooks/useCountUp'

import styles from './PlanningStatusLine.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planningStatus

const EMPTY_VALUE = '--'

/* The figures are marked inside the phrase, so they stand out of the words around them. */
const countParams = (count: number, total: number) => [
  { key: '{count}', value: `<strong>${count}</strong>` },
  { key: '{total}', value: `<strong>${total}</strong>` },
]

const Separator = () => (
  <span className={styles.separator} aria-hidden="true">
    ·
  </span>
)

interface IPlanningStatusLineProps {
  status: ICockpitPlanningStatus
  /** `row` is the line under the tabs; `column` stacks the three parts, as in a tab's tooltip. */
  layout?: 'row' | 'column'
  /** The figures count up, the needle swings and the parts rise in turn, as the line appears. */
  animated?: boolean
  /** Light text and needle for a dark ground. */
  inverted?: boolean
  /** Opens the plan quality review: a scored plan's quality becomes the way to it. */
  onQualityClick?: () => void
}

/** How complete a programme's plan is, whether the PBM has signed it and its plan quality. */
export const PlanningStatusLine = ({
  status,
  layout = 'row',
  animated = false,
  inverted = false,
  onQualityClick,
}: IPlanningStatusLineProps) => {
  const { completeness, acknowledgment, score } = status
  const percent = useCountUp('percent' in completeness ? completeness.percent : null, animated)
  /* A count over no programme, like a share nobody measured, is a dash rather than `0 of 0`. */
  const isCompletenessUnknown =
    'percent' in completeness ? completeness.percent === null : completeness.total === 0
  const isAcknowledgmentUnknown =
    'count' in acknowledgment ? acknowledgment.total === 0 : acknowledgment.acknowledged === null
  const shownScore = useCountUp(score, animated)
  const isRow = layout === 'row'

  return (
    <div
      className={cn(styles.root, styles[layout], {
        [styles.animated]: animated,
        [styles.inverted]: inverted,
      })}
    >
      <span className={styles.item}>
        <span className={styles.value}>
          {isCompletenessUnknown ? (
            EMPTY_VALUE
          ) : 'percent' in completeness ? (
            <strong>{`${Math.round(percent ?? 0)}%`}</strong>
          ) : (
            <Translation
              text={KEYS.count}
              replaceParams={countParams(completeness.count, completeness.total)}
              span
            />
          )}
        </span>
        <Translation text={KEYS.complete} span />
      </span>
      {isRow && <Separator />}
      <span className={styles.item}>
        {isAcknowledgmentUnknown ? (
          <>
            <Translation text={KEYS.acknowledgment} span />
            <span className={styles.value}>{EMPTY_VALUE}</span>
          </>
        ) : 'count' in acknowledgment ? (
          <Translation
            text={KEYS.acknowledgedCount}
            replaceParams={countParams(acknowledgment.count, acknowledgment.total)}
            span
          />
        ) : acknowledgment.acknowledged ? (
          <Translation
            text={KEYS.acknowledgedOn}
            replaceParams={[{ key: '{date}', value: acknowledgment.date ?? '--' }]}
            span
          />
        ) : (
          <Translation text={KEYS.notAcknowledged} span />
        )}
      </span>
      {isRow && <Separator />}
      {score !== null && onQualityClick ? (
        <button
          type="button"
          className={cn(styles.item, styles.qualityLink)}
          onClick={onQualityClick}
        >
          <QualityGauge score={score} animated={animated} inverted={inverted} />
          <span className={styles.qualityText}>
            <Translation text={KEYS.planQuality} span />{' '}
            <strong>{formatCockpitScore(shownScore)}</strong>
          </span>
          <i className="kbase kbase-chevron-right" aria-hidden="true" />
        </button>
      ) : (
        <span className={styles.item}>
          {/* With no score the needle has nothing to point at, so only the dash is shown. */}
          {score !== null && <QualityGauge score={score} animated={animated} inverted={inverted} />}
          <Translation text={KEYS.planQuality} span />
          <span className={styles.value}>
            {score === null ? (
              formatCockpitScore(shownScore)
            ) : (
              <strong>{formatCockpitScore(shownScore)}</strong>
            )}
          </span>
        </span>
      )}
    </div>
  )
}

PlanningStatusLine.displayName = 'PlanningStatusLine'
